import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, ChatBubbleLeftIcon, PaperAirplaneIcon, MagnifyingGlassIcon, XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

import PortfolioViewModalV2 from '../profile/PortfolioViewModalV2';
import CommentItemV2 from '../common/CommentItemV2';
import Avatar from '../common/Avatar';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchPortfoliosPage,
  togglePortfolioLike,
  fetchCommentsPage,
  addPortfolioComment,
  addCommentReply,
} from '../../store/slices/portfolioSlice';

import { Portfolio } from '../../types';
import { Link } from 'react-router-dom';
import { userRelationshipAPI, memoryBoardAPI } from '../../services/api';
import { updateUser } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';
import { fetchCollaborations } from '../../store/slices/collaborationSlice';
import { Collaboration } from '../../types';
import CollaborationShowcaseModal from '../collaboration/CollaborationShowcaseModal';
import { UserGroupIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/image';
import MemoryBoardCard from './MemoryBoardCard';
import LocationPicker from '../common/LocationPicker';

// -------------------- Constants --------------------
const PIN_STYLES = [
  { color: 'linear-gradient(135deg, #1f2937, #000000)', shadow: 'rgba(0,0,0,0.3)' }, // Black Metal
  { color: 'linear-gradient(135deg, #9ca3af, #4b5563)', shadow: 'rgba(0,0,0,0.2)' }, // Silver
  { color: 'linear-gradient(135deg, #d1d5db, #9ca3af)', shadow: 'rgba(0,0,0,0.2)' }, // Light Silver
  { color: 'linear-gradient(135deg, #ef4444, #b91c1c)', shadow: 'rgba(239, 68, 68, 0.3)' }, // Red Pushpin (Classic)
];

// Helper for stable random values based on ID
const getRandomStyle = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash |= 0;
  }
  const seededRandom = () => {
    const x = Math.sin(hash++) * 10000;
    return x - Math.floor(x);
  };
  
  return {
    rotation: (seededRandom() * 2) - 1, // Reduced tilt to +/- 1 degree as requested
    pinStyle: PIN_STYLES[Math.floor(seededRandom() * PIN_STYLES.length)],
    offsetY: seededRandom() * 10, // Reduced vertical offset
  };
};

// Helper for short time format
const formatShortTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
  return `${Math.floor(diffInSeconds / 604800)}w`;
};



interface MemoryBoardProps {
  className?: string;
}

const MemoryBoard: React.FC<MemoryBoardProps> = ({ className = '' }) => {
  const dispatch = useAppDispatch();
  const { allPortfolios, loading, hasMorePortfolios, currentPage, portfolioComments, commentsLoading } =
    useAppSelector((s) => s.portfolio);
  const { user } = useAppSelector((s) => s.auth);
  const { collaborations } = useAppSelector((s) => s.collaboration);

  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [submittingComments, setSubmittingComments] = useState<Record<string, boolean>>({});
  const [replyingTo, setReplyingTo] = useState<{portfolioId: string, commentId: string, userName: string} | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  const [followLoading, setFollowLoading] = useState(false);
  const [followMap, setFollowMap] = useState<Record<string, boolean>>({});
  const [newlyFollowedUsers, setNewlyFollowedUsers] = useState<Set<string>>(new Set());

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchCoordinates, setSearchCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const [searchRadius, setSearchRadius] = useState<number>(50);
  const [isSearching, setIsSearching] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchType, setSearchType] = useState<'all' | 'portfolio' | 'collaboration'>('all');

  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Responsive Columns Logic
  const [numColumns, setNumColumns] = useState(4);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 640) setNumColumns(1);
      else if (width < 1024) setNumColumns(2);
      else if (width < 1280) setNumColumns(3);
      else if (width < 1536) setNumColumns(4);
      else setNumColumns(5);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Merge and sort by date
  const mixedItems = useMemo(() => {
    if (searchActive) {
      return searchResults;
    }

    const portfolios = allPortfolios.map(p => ({ ...p, type: 'portfolio' as const }));
    
    // Sort collaborations by date descending
    const sortedCollabs = [...collaborations]
        .map(c => ({ ...c, type: 'collaboration' as const }))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const merged: ((Portfolio & { type: 'portfolio' }) | (Collaboration & { type: 'collaboration' }))[] = [];
    let cIndex = 0;

    // Interleave collaborations into portfolios based on date, 
    // BUT strictly preserving the portfolio order (which is determined by Smart Feed backend)
    for (const p of portfolios) {
        const pDate = new Date(p.created_at).getTime();
        
        // Insert all collaborations that are newer than this portfolio
        while (cIndex < sortedCollabs.length) {
            const cDate = new Date(sortedCollabs[cIndex].created_at).getTime();
            if (cDate > pDate) {
                merged.push(sortedCollabs[cIndex]);
                cIndex++;
            } else {
                break;
            }
        }
        merged.push(p);
    }

    // Append any remaining collaborations
    while (cIndex < sortedCollabs.length) {
        merged.push(sortedCollabs[cIndex]);
        cIndex++;
    }
    
    return merged.filter((item: any) => {
      const userObj = item.type === 'collaboration' ? item.creator : item.user;
      if (!userObj) return false;
      if (userObj.deleted_at || userObj.deletion_scheduled_at || userObj.is_disabled) return false;
      if (!userObj.first_name && !userObj.name) return false;
      return true;
    });
  }, [allPortfolios, collaborations, searchActive, searchResults]);

  const knownItems = useMemo(() => mixedItems.filter(i => 'is_known' in i && i.is_known), [mixedItems]);
  const unknownItems = useMemo(() => mixedItems.filter(i => !('is_known' in i && i.is_known)), [mixedItems]);

  // Distribute items into columns
  const columns = useMemo(() => {
    const cols: ((Portfolio & { type: 'portfolio' }) | (Collaboration & { type: 'collaboration' }))[][] = Array.from({ length: numColumns }, () => []);
    mixedItems.forEach((item, index) => {
      cols[index % numColumns].push(item);
    });
    return cols;
  }, [mixedItems, numColumns]);

  // Initial load
  useEffect(() => {
    if (allPortfolios.length === 0) {
        dispatch(fetchPortfoliosPage({ page: 1, limit: 12 }));
    }
    if (collaborations.length === 0) {
      dispatch(fetchCollaborations({ is_public: true }));
    }
  }, [dispatch, allPortfolios.length, collaborations.length]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePortfolios && !loading && !loadingMore) {
          setLoadingMore(true);
          dispatch(fetchPortfoliosPage({ page: currentPage + 1, limit: 12 }))
            .finally(() => setLoadingMore(false));
        }
      },
      { rootMargin: '800px' }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [hasMorePortfolios, loading, loadingMore, currentPage, dispatch, searchActive]);

  // Handlers
  const handleLike = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(togglePortfolioLike(id));
    },
    [dispatch]
  );

  const handleCommentToggle = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (expandedCard === id) {
        setExpandedCard(null);
      } else {
        setExpandedCard(id);
        const userId = user?.id || '';
        dispatch(fetchCommentsPage({ portfolioId: id, page: 1, userId }));
      }
    },
    [dispatch, expandedCard, user]
  );

  const handleCommentSubmit = useCallback(
    async (e: React.FormEvent, id: string) => {
      e.preventDefault();
      const text = (commentTexts[id] || '').trim();
      if (!text) return;
      setSubmittingComments((prev) => ({ ...prev, [id]: true }));
      try {
        await dispatch(addPortfolioComment({ portfolioId: id, content: text })).unwrap();
        setCommentTexts((prev) => ({ ...prev, [id]: '' }));
        const userId = user?.id || '';
        dispatch(fetchCommentsPage({ portfolioId: id, page: 1, userId }));
      } catch (err) {
        console.error('add comment failed', err);
      } finally {
        setSubmittingComments((prev) => ({ ...prev, [id]: false }));
      }
    },
    [commentTexts, dispatch, user]
  );

  const handleReply = useCallback((comment: any, portfolioId: string) => {
    setReplyingTo({
      portfolioId,
      commentId: comment.id,
      userName: `${comment.user?.first_name} ${comment.user?.last_name}`
    });
    setReplyText('');
  }, []);

  const handleReplySubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyingTo || !replyText.trim()) return;
    
    setSubmittingReply(true);
    try {
      await dispatch(addCommentReply({
        commentId: replyingTo.commentId,
        content: replyText.trim()
      })).unwrap();
      
      setReplyText('');
      setReplyingTo(null);
      
      // Refresh comments
      const userId = user?.id || '';
      dispatch(fetchCommentsPage({ portfolioId: replyingTo.portfolioId, page: 1, userId }));
    } catch (err) {
      console.error('Failed to add reply:', err);
    } finally {
      setSubmittingReply(false);
    }
  }, [replyingTo, replyText, dispatch, user]);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyText('');
  }, []);

  const handleFollowToggle = useCallback(async (item: Portfolio) => {
    if (!item.user) return;
    
    const userId = item.user.id;
    // Determine current state: check followMap first, then fallback to item prop
    const currentIsFollowing = followMap[userId] ?? item.user.do_i_follow ?? false;
    const newIsFollowing = !currentIsFollowing;

    // Optimistic Update
    setFollowMap(prev => ({ ...prev, [userId]: newIsFollowing }));
    
    // Update newly followed set for UI consistency
    if (newIsFollowing) {
        setNewlyFollowedUsers(prev => new Set(prev).add(userId));
    } else {
        setNewlyFollowedUsers(prev => {
            const updated = new Set(prev);
            updated.delete(userId);
            return updated;
        });
    }

    try {
      const response = await userRelationshipAPI.toggleFollow(userId);
      
      if (response.data.success) {
        const serverIsFollowing = response.data.data.following;
        
        // Verify server state matches our optimistic update
        if (serverIsFollowing !== newIsFollowing) {
             setFollowMap(prev => ({ ...prev, [userId]: serverIsFollowing }));
             
             // Correct newly followed set if mismatch
             if (serverIsFollowing) {
                setNewlyFollowedUsers(prev => new Set(prev).add(userId));
             } else {
                setNewlyFollowedUsers(prev => {
                    const updated = new Set(prev);
                    updated.delete(userId);
                    return updated;
                });
             }
        }
        
        if (user) {
          const updatedFollowingCount = serverIsFollowing 
            ? (user.following_count || 0) + 1 
            : Math.max((user.following_count || 0) - 1, 0);
          dispatch(updateUser({ ...user, following_count: updatedFollowingCount }));
        }
        
        toast.success(serverIsFollowing ? 'Following!' : 'Unfollowed');
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      toast.error('Failed to update follow status');
      
      // Revert optimistic update on error
      setFollowMap(prev => ({ ...prev, [userId]: currentIsFollowing }));
      
      if (currentIsFollowing) {
        setNewlyFollowedUsers(prev => new Set(prev).add(userId));
      } else {
        setNewlyFollowedUsers(prev => {
            const updated = new Set(prev);
            updated.delete(userId);
            return updated;
        });
      }
    }
  }, [followMap, dispatch, user]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery && !searchLocation) {
      setSearchActive(false);
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    setSearchActive(true);
    try {
      const res = await memoryBoardAPI.search({
        q: searchQuery,
        lat: searchCoordinates?.lat,
        lng: searchCoordinates?.lng,
        radius: searchCoordinates ? searchRadius : undefined,
        type: searchType !== 'all' ? searchType : undefined,
        page: 1,
        limit: 50
      });

      if (res.data?.success && res.data?.data?.items) {
          const normalized = res.data.data.items.map((i: any) => ({
              ...i.item,
              type: i.type,
              distance_km: i.distance_km
          }));
          setSearchResults(normalized);
      } else {
          setSearchResults([]);
      }
    } catch (err) {
      console.error('Search failed', err);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, searchLocation, searchCoordinates, searchRadius]);
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchLocation('');
    setSearchCoordinates(null);
    setSearchActive(false);
    setSearchResults([]);
  };

  // Find the index where we should show the separator
  const separatorIndex = useMemo(() => {
    if (knownItems.length === 0 || unknownItems.length === 0) return -1;
    return knownItems.length;
  }, [knownItems.length, unknownItems.length]);

  return (
    <div className={`max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-4 ${className}`}>
      {/* Page Heading */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">Memory Board</h1>
        <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5">Explore portfolios, collaborations, and shared moments</p>
      </div>

      {/* Search Bar UI */}
      <div className="mb-8 max-w-4xl mx-auto">
        {/* <div className="flex flex-col md:flex-row gap-4 items-center bg-white dark:bg-gray-800 p-2 md:pl-6 rounded-2xl md:rounded-full shadow-lg border border-gray-100 dark:border-gray-700 transition-all focus-within:ring-2 focus-within:ring-primary-500/20">
            <div className="flex-1 w-full flex items-center relative gap-3">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 shrink-0" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by keywords, tags, or style..."
                    className="w-full bg-transparent border-none text-gray-900 dark:text-white placeholder-gray-400 py-3"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
            
            <div className="hidden md:block w-px h-8 bg-gray-200 dark:bg-gray-700" />
            
            <div className="w-full md:w-auto flex items-center gap-2 pr-2">
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                        showFilters || searchCoordinates ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                >
                    <AdjustmentsHorizontalIcon className="w-4 h-4" />
                    Location
                </button>
                
                <button
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="px-6 py-2.5 bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 text-sm font-medium rounded-full shadow-sm transition-all disabled:opacity-50"
                >
                    {isSearching ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Search'}
                </button>
            </div>
        </div> */}

        <AnimatePresence>
            {showFilters && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-4 p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700"
                >
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Content Type
                            </label>
                            <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl w-fit">
                                <button
                                    onClick={() => setSearchType('all')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${searchType === 'all' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setSearchType('portfolio')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${searchType === 'portfolio' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Portfolios
                                </button>
                                <button
                                    onClick={() => setSearchType('collaboration')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${searchType === 'collaboration' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Projects
                                </button>
                            </div>
                        </div>

                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Search near location
                            </label>
                            <div className="relative z-50">
                                <LocationPicker
                                    value={searchLocation}
                                    onChange={(loc, coords) => {
                                        setSearchLocation(loc);
                                        if (coords) setSearchCoordinates(coords);
                                    }}
                                    placeholder="Enter a city or place..."
                                />
                            </div>
                        </div>
                        {searchCoordinates && (
                            <div className="w-full md:w-48">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Radius: {searchRadius}km
                                </label>
                                <input
                                    type="range"
                                    min="5"
                                    max="500"
                                    step="5"
                                    value={searchRadius}
                                    onChange={(e) => setSearchRadius(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>5km</span>
                                    <span>500km</span>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>

        {searchActive && (
            <div className="mt-4 flex items-center justify-between text-sm text-gray-500 flex-wrap gap-2">
                <span>Found {searchResults.length} results</span>
                <button
                    onClick={clearSearch}
                    className="text-primary-500 hover:text-primary-600 font-medium"
                >
                    Clear search
                </button>
            </div>
        )}
      </div>

      <div className="flex gap-4">
        {columns.map((col, colIndex) => (
          <div key={colIndex} className="flex-1 flex flex-col gap-4 min-w-0">
            {col.map((item, itemIndexInCol) => {
              const isCollaboration = 'type' in item && item.type === 'collaboration';
              const isExpanded = expandedCard === item.id;
              const style = getRandomStyle(item.id);
              
              // Calculate global index to determine if separator should appear before this item
              const globalIndex = mixedItems.findIndex(i => i.id === item.id);
              const showSeparatorBefore = globalIndex === separatorIndex && colIndex === 0;

              return (
                <React.Fragment key={item.id}>
                  {/* Full-Width Separator - show only in first column at transition point and only on mobile
                  {showSeparatorBefore && separatorIndex > 0 && (
                    <div className="col-span-full w-screen relative left-1/2 right-1/2 -mx-[50vw] py-12 my-8 block sm:hidden">
                      <div className="max-w-[1920px] mx-auto px-4">
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center" aria-hidden="true">
                            <div className="w-full border-t-2 border-gray-300"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-white dark:bg-gray-800 px-6 py-2 text-base font-semibold text-gray-600 dark:text-gray-400 rounded-full border-2 border-gray-300 dark:border-gray-700 shadow-sm">
                              ✨ You're all caught up! ✨
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )} */}

                  <MemoryBoardCard
                    item={item}
                    style={style}
                    isExpanded={isExpanded}
                    currentUser={user}
                    isFollowing={followMap[(item as any).user?.id] ?? (item as any).user?.do_i_follow ?? false}
                    onToggleExpand={(e) => handleCommentToggle(e, item.id)}
                    onLike={(e) => handleLike(e, item.id)}
                    onFollow={(item) => handleFollowToggle(item)}
                    onSelectPortfolio={setSelectedPortfolio}
                    onSelectCollaboration={setSelectedCollaboration}
                  >
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                          {/* Comments List */}
                          <div className="space-y-4 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                            {(commentsLoading && expandedCard === item.id) ? (
                              <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm">Loading comments...</div>
                            ) : portfolioComments[item.id]?.length > 0 ? (
                              portfolioComments[item.id].map((comment) => (
                                <CommentItemV2
                                  key={comment.id}
                                  comment={comment}
                                  commentId={comment.id}
                                  portfolioId={item.id}
                                  portfolioOwnerId={(item as any).user?.id}
                                  onReply={() => handleReply(comment, item.id)}
                                  isCompact={true}
                                />
                              ))
                            ) : (
                              <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">
                                No comments yet. Be the first!
                              </div>
                            )}
                          </div>

                          {/* Comment Input */}
                          <form onSubmit={(e) => handleCommentSubmit(e, item.id)} className="mt-4 relative">
                            {replyingTo && replyingTo.portfolioId === item.id ? (
                                <div className="mb-2 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Replying to <span className="font-bold text-gray-900 dark:text-white">{replyingTo.userName}</span></span>
                                    <button 
                                        type="button"
                                        onClick={handleCancelReply}
                                        className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : null}
                            
                            <div className="relative">
                                <input
                                  type="text"
                                  value={replyingTo && replyingTo.portfolioId === item.id ? replyText : (commentTexts[item.id] || '')}
                                  onChange={(e) => {
                                      if (replyingTo && replyingTo.portfolioId === item.id) {
                                          setReplyText(e.target.value);
                                      } else {
                                          setCommentTexts((prev) => ({ ...prev, [item.id]: e.target.value }));
                                      }
                                  }}
                                  placeholder={replyingTo && replyingTo.portfolioId === item.id ? "Write a reply..." : "Add a comment..."}
                                  className="w-full pl-4 pr-10 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 focus:border-primary-300 dark:focus:border-primary-700 transition-all"
                                />
                                <button
                                  type="submit"
                                  disabled={submittingComments[item.id] || submittingReply}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors disabled:opacity-50"
                                >
                                  <PaperAirplaneIcon className="w-4 h-4" />
                                </button>
                            </div>
                          </form>
                        </div>
                  </MemoryBoardCard>
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </div>
      {/* Loading Sentinel */}
      <div ref={sentinelRef} className="h-20 flex items-center justify-center mt-8">
        {loadingMore && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
            <span className="text-sm">Loading more memories...</span>
          </div>
        )}
      </div>
      
      {!hasMorePortfolios && allPortfolios.length > 0 && (
          <div className="text-center text-gray-400 dark:text-gray-500 text-sm py-8">
              You've reached the end of the memory lane.
          </div>
      )}

      {/* Full View Modal */}
      <AnimatePresence>
        {selectedPortfolio && (
          <PortfolioViewModalV2
            portfolioId={selectedPortfolio.id}
            onClose={() => setSelectedPortfolio(null)}
            isOwnProfile={false}
          />
        )}
        {selectedCollaboration && (
          <CollaborationShowcaseModal
            collaboration={selectedCollaboration}
            onClose={() => setSelectedCollaboration(null)}
            onNextCollaboration={() => {
              const sortedCollaborations = mixedItems.filter((item): item is Collaboration & { type: 'collaboration' } => item.type === 'collaboration');
              const idx = sortedCollaborations.findIndex(c => c.id === selectedCollaboration!.id);
              if (idx < sortedCollaborations.length - 1) {
                setSelectedCollaboration(sortedCollaborations[idx + 1]);
              }
            }}
            onPreviousCollaboration={() => {
              const sortedCollaborations = mixedItems.filter((item): item is Collaboration & { type: 'collaboration' } => item.type === 'collaboration');
              const idx = sortedCollaborations.findIndex(c => c.id === selectedCollaboration!.id);
              if (idx > 0) {
                setSelectedCollaboration(sortedCollaborations[idx - 1]);
              }
            }}
            hasNext={(() => {
              const sortedCollaborations = mixedItems.filter((item): item is Collaboration & { type: 'collaboration' } => item.type === 'collaboration');
              const idx = sortedCollaborations.findIndex(c => c.id === selectedCollaboration!.id);
              return idx < sortedCollaborations.length - 1;
            })()}
            hasPrevious={(() => {
              const sortedCollaborations = mixedItems.filter((item): item is Collaboration & { type: 'collaboration' } => item.type === 'collaboration');
              const idx = sortedCollaborations.findIndex(c => c.id === selectedCollaboration!.id);
              return idx > 0;
            })()}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MemoryBoard;
